import FieldNames from './FieldNames.json';
import type { GridFieldConfiguration } from '../utils/GridColumnHelper.ts';
import type { GridRenderCellParams } from '@mui/x-data-grid';
import { formatCurrency, formatResponse, getURLPath } from '../utils/GridHelper';
import type { GridAggregationModel, GridAggregationFunctionDataSource } from '@mui/x-data-grid-premium';

export const bulkEditFeatureFields: GridFieldConfiguration[] = [
    { field: FieldNames.featureFields.name, editable: false },
    {
        field: FieldNames.featureFields.objectID,
        editable: false,
        alias: 'ID',
        renderCell: (params: GridRenderCellParams) => (
            <a target='_blank' rel='noopener noreferrer' href={getURLPath(params.row, 'ID')}>
                {formatResponse(params.row, 'ID')}
            </a>
        ),
    },
    {
        field: FieldNames.featureFields.fundingSourceProgram,
        editable: false,
        // valueFormatter: (v, r, c) => formatAttributesFundingField(v, c),
    },
    {
        field: FieldNames.featureFields.fundingSourceCategory,
        editable: false,
        // valueFormatter: (v, r, c) => formatAttributesFundingField(v, c),
    },
    {
        field: FieldNames.featureFields.fundingSourceSubCategory,
        editable: false,
        // valueFormatter: (v, r, c) => formatAttributesFundingField(v, c),
    },
    // fiscal year is stored as a number and MUI grid renders it with a comma, so we need to format it to display without the comma. It is not editable, so we don't need to worry about parsing the value back to a number.
    {
        field: FieldNames.featureFields.initiationFiscalYear,
        editable: false,
        valueFormatter: (value: number) => (value ? value.toString() : null),
    },
    {
        field: FieldNames.featureFields.fundingAgency,
        editable: false,
        // valueFormatter: (v, r, c) => formatAttributesFundingField(v, c),
    },
    {
        field: FieldNames.featureFields.fundingRegion,
        editable: false,
        // valueFormatter: (v, r, c) => formatAttributesFundingField(v, c),
    },
    {
        field: FieldNames.featureFields.fundingUnit,
        editable: false,
        // valueFormatter: (v, r, c) => formatAttributesFundingField(v, c),
    },
    { field: FieldNames.featureFields.class, editable: false },
    { field: FieldNames.featureFields.localApprovalDate, editable: false },
    { field: FieldNames.featureFields.regionalApprovalDate, editable: false },
    { field: FieldNames.featureFields.agencyApprovalDate, editable: false },
    { field: FieldNames.featureFields.departmentApprovalDate, editable: false },
];

export const bulkEditFundingFields: GridFieldConfiguration[] = [
    {
        field: FieldNames.fundingFields.id,
        editable: false,
        valueFormatter: (value: number) => (value ? value.toString() : null),
    },
    { field: FieldNames.fundingFields.fundingSource, editable: false },
    { field: FieldNames.fundingFields.fundingSourceProgram, editable: false },
    { field: FieldNames.fundingFields.fundingSourceCategory, editable: false },
    { field: FieldNames.fundingFields.fundingSourceSubCategory, editable: false },
    { field: FieldNames.fundingFields.fundingDepartment, editable: false },
    { field: FieldNames.fundingFields.fundingAgency, editable: false },
    { field: FieldNames.fundingFields.fundingRegion, editable: false },
    { field: FieldNames.fundingFields.fundingUnit, editable: false },
    { field: FieldNames.fundingFields.fundingSubUnit, editable: false },
    { field: FieldNames.fundingFields.fundingTribe, editable: false },
    { field: FieldNames.fundingFields.fundingUnitType, editable: false },
    { field: FieldNames.fundingFields.fundingUnitID, editable: false },
    {
        field: FieldNames.fundingFields.personnelCost,
        editable: false,
        valueFormatter: (value) => formatCurrency(value),
    },
    {
        field: FieldNames.fundingFields.grantsFixedCost,
        editable: false,
        valueFormatter: (value) => formatCurrency(value),
    },
    {
        field: FieldNames.fundingFields.assetCost,
        editable: false,
        valueFormatter: (value) => formatCurrency(value),
    },
    {
        field: FieldNames.fundingFields.contractualCost,
        editable: false,
        valueFormatter: (value) => formatCurrency(value),
    },
    {
        field: FieldNames.fundingFields.otherCost,
        editable: false,
        valueFormatter: (value) => formatCurrency(value),
    },
    {
        field: FieldNames.fundingFields.equipmentCost,
        editable: false,
        valueFormatter: (value) => formatCurrency(value),
    },
    {
        field: FieldNames.fundingFields.materialAndSupplyCost,
        editable: false,
        valueFormatter: (value) => formatCurrency(value),
    },
    {
        field: FieldNames.fundingFields.procurementCost,
        editable: false,
        valueFormatter: (value) => formatCurrency(value),
    },
    {
        field: FieldNames.fundingFields.travelCost,
        editable: false,
        valueFormatter: (value) => formatCurrency(value),
    },
    {
        field: FieldNames.fundingFields.totalCost,
        editable: false,
        valueFormatter: (value) => formatCurrency(value),
    },
    { field: FieldNames.fundingAcreageFields.acres, editable: false },
    { field: FieldNames.fundingFields.modifiedBy, editable: false },
    { field: FieldNames.fundingFields.modifiedDate, editable: false },
    { field: FieldNames.fundingFields.createdBy, editable: false },
    { field: FieldNames.fundingFields.createdDate, editable: false },
    { field: FieldNames.fundingFields.notes, editable: false },
    { field: FieldNames.fundingFields.objectID, editable: false },
];

export const fundingGridAggregationModel: GridAggregationModel = {
    [FieldNames.fundingFields.personnelCost]: 'sum',
    [FieldNames.fundingFields.grantsFixedCost]: 'sum',
    [FieldNames.fundingFields.assetCost]: 'sum',
    [FieldNames.fundingFields.contractualCost]: 'sum',
    [FieldNames.fundingFields.otherCost]: 'sum',
    [FieldNames.fundingFields.equipmentCost]: 'sum',
    [FieldNames.fundingFields.materialAndSupplyCost]: 'sum',
    [FieldNames.fundingFields.procurementCost]: 'sum',
    [FieldNames.fundingFields.travelCost]: 'sum',
    [FieldNames.fundingFields.totalCost]: 'sum',
};

/**
 * Server-side aggregation function definitions.
 * When a dataSource is provided, the DataGrid expects GridAggregationFunctionDataSource entries
 * (no `apply` callback — the server computes the values via aggregateRow in the getRows response).
 * These definitions supply labels and value formatting for the footer cells.
 */
export const fundingGridAggregationFunctions: Record<string, GridAggregationFunctionDataSource> = {
    sum: {
        label: 'Sum',
        columnTypes: ['number'],
        valueFormatter: (value) => formatCurrency(value),
    },
};
// tbd if we use this, the generally display "Multiple x values" for different funding fields with multiple sources, programs, etc. but makes sense to actually see those values during editing
// function formatAttributesFundingField(value: string | null, column: GridColDef) {
//     return getFieldDisplayValue(value, column.field, FieldNames.featureFields);
// }
//
